import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuthStore } from '@/store/auth';
import type { OcppFrameRow } from './api';

/**
 * Live OCPP frame stream from the OCPP server's `/admin-stream`
 * WebSocket. Returns the rolling buffer (newest-first), plus
 * connection state and a manual `clear`. Capped at `bufferSize`
 * frames (default 500) so a chatty charger doesn't blow up the
 * tab over a long session.
 *
 * Pause-on-demand: pass `enabled=false` to stop appending without
 * tearing down the socket. The socket itself is torn down only
 * when `chargerIdentity` changes or the consumer unmounts.
 *
 * URL is configured by VITE_OCPP_STREAM_URL — defaults to
 * `ws://localhost:8080/admin-stream` for local dev. Production
 * sets it to `wss://ws.evinitiative.com/admin-stream`. Identity
 * + JWT travel as query params (no other portable way to send
 * auth on a browser WS handshake).
 */

export type LiveFrameState =
  | { status: 'idle' }
  | { status: 'connecting' }
  | { status: 'open' }
  | { status: 'closed'; reason: string }
  | { status: 'error'; reason: string };

export interface UseLiveFramesResult {
  frames: OcppFrameRow[];
  state: LiveFrameState;
  clear: () => void;
}

const BASE_STREAM_URL =
  (import.meta.env['VITE_OCPP_STREAM_URL'] as string | undefined)
  ?? 'ws://localhost:8080/admin-stream';

interface IncomingFrameMsg {
  type: 'frame';
  chargerIdentity: string;
  direction: 'incoming' | 'outgoing';
  messageType: number;
  messageId: string;
  action: string;
  payload: Record<string, unknown>;
  errorCode?: string;
  errorDescription?: string;
  ocppVersion: string;
  timestamp: string;
}

function frameMsgToRow(msg: IncomingFrameMsg): OcppFrameRow {
  // The wire shape uses camelCase; the historical /logs endpoint
  // uses snake_case (Mongo doc shape). Normalise to the latter so
  // the LogsPanel can render either source uniformly.
  return {
    _id: `live:${msg.messageId}:${msg.timestamp}`,
    charger_identity: msg.chargerIdentity,
    direction: msg.direction,
    message_type: msg.messageType,
    message_id: msg.messageId,
    action: msg.action,
    payload: msg.payload,
    ...(msg.errorCode !== undefined ? { error_code: msg.errorCode } : {}),
    ...(msg.errorDescription !== undefined ? { error_description: msg.errorDescription } : {}),
    ocpp_version: msg.ocppVersion,
    timestamp: msg.timestamp,
  };
}

export function useLiveFrames(
  chargerIdentity: string | null,
  enabled = true,
  bufferSize = 500,
): UseLiveFramesResult {
  const [frames, setFrames] = useState<OcppFrameRow[]>([]);
  const [state, setState] = useState<LiveFrameState>({ status: 'idle' });
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const wsRef = useRef<WebSocket | null>(null);

  const clear = useCallback(() => setFrames([]), []);

  useEffect(() => {
    if (!chargerIdentity) {
      setState({ status: 'idle' });
      return;
    }

    const token = useAuthStore.getState().accessToken;
    if (!token) {
      setState({ status: 'error', reason: 'Not authenticated' });
      return;
    }

    const url =
      `${BASE_STREAM_URL}?chargerIdentity=${encodeURIComponent(chargerIdentity)}`
      + `&token=${encodeURIComponent(token)}`;

    setState({ status: 'connecting' });
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      setState({ status: 'error', reason: err instanceof Error ? err.message : 'WS failed' });
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => setState({ status: 'open' });
    ws.onerror = () => setState({ status: 'error', reason: 'connection error' });
    ws.onclose = (ev) => {
      setState({
        status: 'closed',
        reason: ev.reason || (ev.code === 1006 ? 'connection lost' : `closed (${ev.code})`),
      });
    };

    ws.onmessage = (ev) => {
      if (!enabledRef.current) return;
      try {
        const msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as
          | IncomingFrameMsg
          | { type: string };
        if (msg.type !== 'frame') return;
        const row = frameMsgToRow(msg as IncomingFrameMsg);
        setFrames((prev) => {
          const next = [row, ...prev];
          return next.length > bufferSize ? next.slice(0, bufferSize) : next;
        });
      } catch {
        // ignore malformed frames — server may add new envelope types
      }
    };

    // Application-level keepalive — the server replies with `pong`.
    const ka = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'ping' })); } catch { /* noop */ }
      }
    }, 25_000);

    return () => {
      clearInterval(ka);
      try { ws.close(1000, 'unmount'); } catch { /* noop */ }
      wsRef.current = null;
    };
  }, [chargerIdentity, bufferSize]);

  return { frames, state, clear };
}
